import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { formatLocaleDate, formatYMD } from '@tg/core/utils/datetimeHelpers';
import { formatGrossSalary } from '@tg/core/utils/stringHelpers';
import { List, Button, PaginationControl } from '@tg/core/components';
import { transformCollectionResponse } from '@tg/core/utils/dataTransformers';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { requestWithDispatch } from '@tg/core/utils/requestHelpers';
import { useResource } from '@tg/core/hooks';
import Form from './AddEditModal';

const initialState = {
  fetching: false,
  data: null,
  error: null,
  ui: {
    create: false,
    update: null,
  },
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'FETCH_BEGIN':
    case 'CREATE_BEGIN':
    case 'UPDATE_BEGIN':
    case 'DELETE_BEGIN':
      return {
        ...state,
        fetching: true,
      };

    case 'FETCH_END':
    case 'CREATE_END':
    case 'UPDATE_END':
    case 'DELETE_END':
      return {
        ...state,
        fetching: false,
      };

    case 'FETCH_SUCCESS':
    case 'CREATE_SUCCESS':
    case 'UPDATE_SUCCESS':
    case 'DELETE_SUCCESS':
      return {
        ...state,
        data: transformCollectionResponse(payload.data),
        ui: initialState.ui,
      };

    case 'FETCH_FAILURE':
    case 'CREATE_FAILURE':
    case 'UPDATE_FAILURE':
    case 'DELETE_FAILURE':
      return {
        ...state,
        error: payload.data,
      };

    case 'CREATE':
      return {
        ...state,
        ui: {
          ...state.ui,
          create: true,
        },
      };

    case 'UPDATE':
      return {
        ...state,
        ui: {
          ...state.ui,
          update: payload,
        },
      };

    case 'CANCEL':
      return {
        ...state,
        ui: initialState,
      };

    default:
      return state;
  }
};

const SalaryList = ({ contractId, currentSalary }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [page, setPage] = useState(1);
  const token = useSelector(s => s.session.aut);
  const call = requestWithDispatch(dispatch, token);
  const { t } = useTranslation(['salary_history']);

  const { pagination } = useResource(
    {
      url: `employees/${contractId}/salaries?page=${page}&pagesize=10`,
    },
    true,
  );

  useEffect(() => {
    if (page) {
      call({
        url: `employees/${contractId}/salaries?page=${page}&pagesize=10`,
        action: 'fetch',
      });
    }
  }, [page]);

  const onCreateSubmit = ({ effective_date, ...rest }) => {
    call({
      url: `employees/${contractId}/salaries`,
      action: 'create',
      method: 'post',
      data: { salary: { effective_date: formatYMD(effective_date), ...rest } },
    }).then(() => setPage(1));
  };
  const onUpdateSubmit = ({ effective_date, ...rest }) => {
    const id = state.ui.update;
    call({
      url: `employees/${contractId}/salaries/${id}`,
      action: 'update',
      method: 'patch',
      data: { salary: { effective_date: formatYMD(effective_date), ...rest } },
    }).then(() => setPage(1));
  };

  const onCancel = () => dispatch({ type: 'CANCEL' });

  const listData = state?.data?.byId ? Object.values(state?.data?.byId) : [];

  const selectEditableData = () => {
    return state.data.byId[state.ui.update];
  };

  return (
    <>
      {state.ui.create && (
        <Form onSubmit={onCreateSubmit} onCancel={onCancel} />
      )}
      {state.ui.update && (
        <Form
          onSubmit={onUpdateSubmit}
          onCancel={onCancel}
          data={selectEditableData}
        />
      )}

      <div className='mb-4'>
        <List
          type='salary'
          headings={[
            { text: t('salary_history:gross_salary') },
            { text: t('salary_history:effective_from'), width: '120px' },
            { text: '', align: 'right' },
          ]}
          items={listData.map(
            ({ id, gross_salary, effective_date, currency, approve }) => {
              return {
                id,
                columns: [
                  <span className='break-words'>{`${
                    currency.code
                  } ${formatGrossSalary(gross_salary)}`}</span>,
                  <span>{formatLocaleDate(effective_date)}</span>,
                  <span className='space-x-2'>
                    {!approve && id !== currentSalary.id && (
                      <span
                        className='font-semibold'
                        style={{ color: 'red', fontSize: '15px' }}
                      >
                        Validation Pending
                      </span>
                    )}
                    {id === currentSalary.id && (
                      <span className='font-semibold'>Current</span>
                    )}
                    {approve === false && (
                      <Button
                        size='small'
                        color='tertiary'
                        onClick={() =>
                          dispatch({ type: 'UPDATE', payload: id })
                        }
                      >
                        Edit
                      </Button>
                    )}
                  </span>,
                ],
              };
            },
          )}
        />
      </div>
      <Button onClick={() => dispatch({ type: 'CREATE' })}>Create new</Button>
      {pagination && (
        <div>
          <PaginationControl {...pagination} onPageChange={setPage} />
        </div>
      )}
    </>
  );
};

SalaryList.propTypes = {
  contractId: PropTypes.string.isRequired,
  currentSalary: PropTypes.shape({
    id: PropTypes.string,
    gross_salary: PropTypes.string,
    currency: PropTypes.shape({
      code: PropTypes.string,
    }),
  }).isRequired,
};

export default SalaryList;
